const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Deduplication: completed results cached, in-flight promises shared
const _cache = new Map();
const _pending = new Map();

async function apiFetch(path) {
  if (_cache.has(path)) return _cache.get(path);
  if (_pending.has(path)) return _pending.get(path);

  const promise = fetch(`${API_BASE}${path}`)
    .then((res) => {
      if (!res.ok) {
        const err = new Error(`API ${res.status}`);
        err.status = res.status;
        throw err;
      }
      return res.json();
    })
    .then((data) => {
      _cache.set(path, data);
      _pending.delete(path);
      return data;
    })
    .catch((err) => {
      _pending.delete(path);
      throw err;
    });

  _pending.set(path, promise);
  return promise;
}

// ── Transformers ──────────────────────────────────────────────────────────────

// Single source of truth for status labels — keeps public pages + admin in sync
const _STATUS_LABELS = {
  completed: 'Tamamlandı',
  ongoing:   'Devam Ediyor',
  planning:  'Planlama',
};

function transformProjectList(p) {
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    status: p.status,
    statusLabel: _STATUS_LABELS[p.status] || p.status_label || p.status,
    location: p.location,
    year: p.year,
    units: p.units,
    area: p.area,
    type: p.type,
    shortDescription: p.short_description,
    coverImage: p.cover_image,
    displayOrder: p.display_order,
    isFeatured: p.is_featured,
    featuredOrder: p.featured_order,
    coordinates: p.lat != null && p.lng != null ? [p.lat, p.lng] : null,
  };
}

function transformGalleryItem(item) {
  return {
    type: item.media_type,      // 'image' | 'video' | 'youtube'
    src: item.url,
    poster: item.poster_url || "",
    thumb: item.thumb_url || item.url,
  };
}

function transformProjectDetail(p) {
  return {
    ...transformProjectList(p),
    coverVideo: p.cover_video || null,
    seoTitle: p.seo_title,
    seoDescription: p.seo_description,
    description: p.description,
    address: p.address,
    coordinates: p.lat != null && p.lng != null ? [p.lat, p.lng] : null,
    technicalDetails: {
      constructionArea: p.construction_area || "",
      greenArea: p.green_area || "",
      blocks: p.blocks || "",
      shopCount: p.shop_count || "",
      floors: p.floors || "",
      delivery: p.delivery || "",
    },
    gallery: (p.gallery || []).map(transformGalleryItem),
    features: (p.features || []).map((f) => f.text),
    materialHighlights: (p.materials || []).map((m) => ({
      title: m.title,
      description: m.description || "",
    })),
    floorPlans: (() => {
      const groups = [];
      const groupMap = new Map();
      (p.floor_plans || []).forEach((fp) => {
        const key = fp.type_title || "";
        if (!groupMap.has(key)) {
          const g = { typeTitle: key || null, plans: [] };
          groupMap.set(key, g);
          groups.push(g);
        }

        // label may be JSON-encoded {h, d} (new format) or plain text (legacy)
        let label = fp.label || "";
        let details = fp.details || []; // from JSONB column when migration is run

        if (label) {
          try {
            const parsed = JSON.parse(label);
            if (parsed && typeof parsed === "object" && ("h" in parsed || "d" in parsed)) {
              label = parsed.h || "";
              // only use label-encoded details if JSONB column is empty
              if (!details.length) {
                details = Array.isArray(parsed.d) ? parsed.d : [];
              }
            }
          } catch { /* plain-text label — keep as-is */ }
        }

        groupMap.get(key).plans.push({ image: fp.image_url, label, details });
      });
      return groups;
    })(),
    nearby: (p.nearby || []).map((n) => n.text),
  };
}

function transformService(s, index) {
  // Preserve testId derivation compatible with the hardcoded service order
  const TEST_IDS = [
    "service-residential",
    "service-commercial",
    "service-engineering",
    "service-consulting",
  ];
  return {
    id: s.id,
    tag: s.tag,
    title: s.title,
    description: s.description,
    image: s.image,
    reverse: s.reverse_layout,
    features: (s.features || []).map((f) => f.text),
    testId: TEST_IDS[index] || `service-${index}`,
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function fetchProjects() {
  const data = await apiFetch("/api/projects");
  return data.map(transformProjectList);
}

export function getProjectsSync() {
  const raw = _cache.get("/api/projects");
  return raw ? raw.map(transformProjectList) : null;
}

export async function fetchFeaturedProjects() {
  const data = await apiFetch("/api/projects/featured");
  return data.map(transformProjectList);
}

export async function fetchProject(slug) {
  const data = await apiFetch(`/api/projects/${encodeURIComponent(slug)}`);
  return transformProjectDetail(data);
}

export async function fetchServices() {
  const data = await apiFetch("/api/services");
  return data.map(transformService);
}

export async function fetchCompanyInfo() {
  return apiFetch("/api/company-info");
}

export function getCompanyInfoSync() {
  return _cache.get("/api/company-info") ?? null;
}

export async function fetchHomepageContent() {
  return apiFetch("/api/homepage-content");
}

// Pre-warm endpoints that every page needs as soon as this module loads.
// Deduplication in apiFetch ensures components re-using the same fetchers
// get the cached result instantly. Errors are swallowed — components handle
// them individually on mount.
fetchProjects().catch(() => {});
fetchCompanyInfo().catch(() => {});

export async function submitDreamRequest(payload) {
  const res = await fetch(`${API_BASE}/api/dreams`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || `Sunucu hatası: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function submitContact(payload) {
  const res = await fetch(`${API_BASE}/api/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || `Sunucu hatası: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
